import React, { useState } from 'react';
import { EntryPointContainer } from 'react-relay';
import { topNavigationEntryPoint } from '@atlassian/jira-atlassian-navigation/entrypoint.tsx';
import { AtlassianNavigation } from '@atlassian/jira-atlassian-navigation/src/ui/index.tsx';
import { JiraAtlassianNavigationSkeleton as TopNavigationSkeleton } from '@atlassian/jira-atlassian-navigation/src/ui/skeleton/index.tsx';
import { JS_EVAL_START } from '@atlassian/jira-common-constants/src/analytics-marks.js';
import { TOP_NAVIGATION } from '@atlassian/jira-entry-point-page-layout-fields/src/index.tsx';
import { usePageLayoutEntryPoint } from '@atlassian/jira-entry-point-page-layout/src/index.tsx';
import { EntryPointReferencePlaceholder } from '@atlassian/jira-entry-point-placeholder/src/index.tsx';
import { stopInitialPageLoadTimingFromPerformanceMarkStart } from '@atlassian/jira-spa-performance-breakdown/src/utils/performance-marks-tools/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useCurrentRoute } from '@atlassian/jira-platform-router-utils/src/index.tsx';
import { useIsHydrateRenderMode } from '@atlassian/jira-ssr-controller/src/use-is-page-hydrating.tsx';

/**
 * Manages the rendering of the navigation component by conditionally displaying either a placeholder
 * or the actual navigation content. This decision is based on the current routing context and
 * the availability of navigation entry points, aiming to enhance the user experience through
 * efficient loading strategies.
 */
export const AsyncAtlassianNavigation = () => {
	const topNavEntryPointReference =
		usePageLayoutEntryPoint<typeof topNavigationEntryPoint>(TOP_NAVIGATION);

	useState(() => {
		if (!__SERVER__ && topNavEntryPointReference) {
			/* @ts-expect-error - TS2339 - We're using JSResourceReference from @atlassian/react-async. */
			topNavigationEntryPoint.root.onComplete(() => {
				stopInitialPageLoadTimingFromPerformanceMarkStart(
					'navigation-primary.bundle-ready',
					'jira-spa.js:eval-stop',
				);
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				const resources = performance.getEntriesByType('mark');
				for (let i = resources.length - 1; i >= 0; i -= 1) {
					if (
						(resources[i].name.startsWith('async-navigation') ||
							resources[i].name.startsWith('async-atlassian-navigation')) &&
						resources[i].name.endsWith(JS_EVAL_START)
					) {
						stopInitialPageLoadTimingFromPerformanceMarkStart(
							'navigation-primary.eval',
							resources[i].name,
						);
						break;
					}
				}
			});
		}
		return undefined;
	});

	const currentRoute = useCurrentRoute();
	const isPageHydrating = useIsHydrateRenderMode();

	return topNavEntryPointReference ? (
		// There is already a Placeholder in parent
		// remove this one when EntryPointPlaceholder merges with Placeholder
		<EntryPointReferencePlaceholder
			name="top_navigation_skeleton"
			fallback={<TopNavigationSkeleton />}
			entryPointReference={topNavEntryPointReference}
			isSSRPlaceholderEnabled={
				!isPageHydrating && fg('ssr_placeholder_on_topnav_and_sidebar_for_backlog')
					? currentRoute.entryPointOptions?.isSSRPlaceholderEnabled
					: undefined
			}
		>
			{topNavEntryPointReference ? (
				<EntryPointContainer
					entryPointReference={topNavEntryPointReference}
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					props={{} as never}
				/>
			) : null}
		</EntryPointReferencePlaceholder>
	) : (
		<AtlassianNavigation />
	);
};
